"use client";

import { talentNetworkText } from "@/i18n/talentNetwork.constants";

import { useId, useLayoutEffect, useRef, useState } from "react";
import { PROFILE_DESCRIPTION_VISIBLE_LINES } from "../profileCards.constants";
import type { ProfileDescriptionProps } from "../profileCards.types";

export function ProfileDescription({ description }: ProfileDescriptionProps) {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const id = useId();

  useLayoutEffect(() => {
    const element = textRef.current!;

    const measure = () => {
      const lineHeight = Number.parseFloat(
        getComputedStyle(element).lineHeight,
      );
      setCanExpand(
        element.scrollHeight > lineHeight * PROFILE_DESCRIPTION_VISIBLE_LINES,
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [description]);

  return (
    <div className="mt-2">
      <p
        ref={textRef}
        id={id}
        className="overflow-hidden whitespace-pre-line break-words text-xs leading-5 text-[#475467] [display:-webkit-box] [-webkit-box-orient:vertical]"
        style={{
          WebkitLineClamp: expanded
            ? "unset"
            : PROFILE_DESCRIPTION_VISIBLE_LINES,
        }}
      >
        {description}
      </p>
      {canExpand ? (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={id}
          className="mt-1 rounded text-xs font-semibold leading-5 text-[#0D41E1] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D41E1] focus-visible:ring-offset-2"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded
            ? talentNetworkText("read_less")
            : talentNetworkText("read_more")}
        </button>
      ) : null}
    </div>
  );
}
